import { cn } from "@faire/prototype-playground/lib/utils";

export interface BrandReviewData {
  rating: number;
  body: string;
  date: string;
  reviewer: string;
  location: string;
  productImageUrls: string[];
}

export interface BrandReviewSummaryData {
  rating: number;
  reviewCount: number;
  distribution: { stars: number; count: number; percent: number }[];
  granular: { label: string; value: number }[];
}

export interface BrandReviewsProps {
  summary: BrandReviewSummaryData;
  reviews: BrandReviewData[];
  totalPages?: number;
  /** When false, hides the Top Shop explainer card above the rating summary. */
  showTopShopCallout?: boolean;
  className?: string;
}

/**
 * Brand "Ratings and reviews" section: score summary with rating distribution
 * and granular metrics on the left, the scrollable review list with purchased
 * products and pagination on the right.
 */
export default function BrandReviews({
  summary,
  reviews,
  totalPages = 1,
  showTopShopCallout = true,
  className,
}: BrandReviewsProps) {
  return (
    <section className={cn("flex flex-col gap-6", className)}>
      <div className="flex flex-row items-center justify-between">
        <h2 className="type-fs-page-heading text-fs-text-primary">
          Ratings and reviews
        </h2>
        <button
          type="button"
          className="border-fs-border-muted text-fs-text-primary type-fs-paragraph flex items-center gap-2 rounded-full border px-4 py-2"
        >
          <SortIcon />
          Relevance
        </button>
      </div>

      <div className="flex flex-col gap-8 lg:flex-row lg:gap-16">
        <ReviewSummaryPanel
          showTopShopCallout={showTopShopCallout}
          summary={summary}
        />
        <div className="flex flex-1 flex-col gap-4">
          {reviews.map((review) => (
            <ReviewCell
              key={`${review.reviewer}-${review.date}`}
              review={review}
            />
          ))}
          <hr className="border-fs-border-muted mt-4 border-t" />
          <Pagination totalPages={totalPages} />
        </div>
      </div>
    </section>
  );
}

function ReviewSummaryPanel({
  summary,
  showTopShopCallout,
}: {
  summary: BrandReviewSummaryData;
  showTopShopCallout: boolean;
}) {
  return (
    <div className="flex w-full flex-col lg:w-[360px] lg:shrink-0">
      {showTopShopCallout ? (
        <div className="bg-fs-surface-secondary flex flex-col items-center rounded-lg p-6 text-center">
          <ShieldIcon className="h-12 w-12" />
          <p className="type-fs-paragraph text-fs-text-primary mt-6">
            Top Shop celebrates exceptional brands who have earned a 4.8+ rating
            over the last 12 months, fulfill orders quickly and smoothly, and
            maintain a fresh catalog.{" "}
            <button type="button" className="underline underline-offset-2">
              Learn more
            </button>
          </p>
        </div>
      ) : null}

      {/* Score */}
      <div
        className={cn(
          "flex flex-row items-center gap-2",
          showTopShopCallout && "mt-6"
        )}
      >
        <StarIcon className="h-5 w-5" />
        <p className="type-fs-display-m text-fs-text-primary">
          {summary.rating.toFixed(1)}
        </p>
      </div>
      <p className="type-fs-paragraph text-fs-text-primary mt-2">
        Brand rating ({summary.reviewCount} reviews)
      </p>

      {/* Distribution */}
      <div className="mt-4 flex flex-col gap-1">
        {summary.distribution.map((row) => (
          <div key={row.stars} className="flex items-center gap-2">
            <p className="type-fs-paragraph text-fs-text-primary w-2">
              {row.stars}
            </p>
            <div className="relative h-1 flex-1 rounded-[3px] bg-(--slate-color-grey-400)">
              <div
                className="bg-fs-surface-primary-inverse absolute top-0 left-0 h-full rounded-[3px]"
                style={{ width: `${row.percent}%` }}
              />
            </div>
            <p
              className={cn(
                "type-fs-paragraph w-6 text-right",
                row.count === 0
                  ? "text-fs-text-subdued"
                  : "text-fs-text-primary"
              )}
            >
              {row.count}
            </p>
          </div>
        ))}
      </div>

      {/* Granular metrics */}
      <div className="mt-8 flex flex-col gap-4 lg:flex-row lg:justify-between">
        {summary.granular.map((metric) => (
          <div
            key={metric.label}
            className="flex items-center justify-between gap-2 lg:flex-col lg:items-start"
          >
            <span className="flex items-center gap-1">
              <StarIcon className="h-3 w-3" />
              <span className="type-fs-paragraph-medium text-fs-text-primary">
                {metric.value.toFixed(1)}
              </span>
            </span>
            <p className="type-fs-paragraph text-fs-text-primary">
              {metric.label}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReviewCell({ review }: { review: BrandReviewData }) {
  return (
    <div className="flex flex-col gap-2">
      <Stars rating={review.rating} />
      <p className="type-fs-paragraph text-fs-text-primary">{review.body}</p>
      <p className="type-fs-label text-fs-text-subdued">
        {review.date} • {review.reviewer} • {review.location}
      </p>
      {review.productImageUrls.length > 0 ? (
        <div className="flex flex-col gap-2">
          <p className="type-fs-label text-fs-text-subdued">
            Purchased{" "}
            {review.productImageUrls.length > 1 ? "products" : "product"}:
          </p>
          <div className="flex flex-row items-center gap-1">
            {review.productImageUrls.map((url) => (
              <img
                key={url}
                src={url}
                alt="Purchased product"
                referrerPolicy="no-referrer"
                className="rounded-fs-component-default h-12 w-12 object-cover"
              />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Stars({ rating }: { rating: number }) {
  return (
    <div
      className="flex flex-row gap-0.5"
      role="img"
      aria-label={`${rating} of 5 stars`}
    >
      {Array.from({ length: 5 }, (_, index) => (
        <StarIcon
          key={index}
          className="h-4 w-4"
          filled={index < Math.round(rating)}
        />
      ))}
    </div>
  );
}

function Pagination({ totalPages }: { totalPages: number }) {
  const pages = Array.from(
    { length: Math.min(totalPages, 5) },
    (_, index) => index + 1
  );
  return (
    <div className="flex flex-row items-center justify-center gap-2">
      <ArrowButton direction="prev" disabled />
      {pages.map((page) => (
        <button
          key={page}
          type="button"
          className={cn(
            "type-fs-paragraph flex h-8 w-8 items-center justify-center rounded-full",
            page === 1
              ? "bg-fs-surface-primary-inverse text-fs-text-primary-inverse"
              : "text-fs-text-primary"
          )}
        >
          {page}
        </button>
      ))}
      {totalPages > 5 ? (
        <>
          <span className="type-fs-paragraph text-fs-text-subdued">…</span>
          <button
            type="button"
            className="type-fs-paragraph text-fs-text-primary flex h-8 w-8 items-center justify-center rounded-full"
          >
            {totalPages}
          </button>
        </>
      ) : null}
      <ArrowButton direction="next" />
    </div>
  );
}

function ArrowButton({
  direction,
  disabled = false,
}: {
  direction: "prev" | "next";
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label={direction === "prev" ? "Previous page" : "Next page"}
      disabled={disabled}
      className={cn(
        "text-fs-text-primary flex h-8 w-8 items-center justify-center",
        disabled && "opacity-30"
      )}
    >
      <svg
        className="h-4 w-4"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        style={{
          transform: direction === "prev" ? "rotate(180deg)" : undefined,
        }}
      >
        <path d="M0.857 12H23.143" />
        <path d="M17.143 18 23.143 12 17.143 6" />
      </svg>
    </button>
  );
}

function StarIcon({
  className,
  filled = true,
}: {
  className?: string;
  filled?: boolean;
}) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill={filled ? "#333333" : "none"}
      stroke="#333333"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m12.844 1.87 2.713 5.501a.94.94 0 0 0 .708.515l6.072.884a.94.94 0 0 1 .515 1.603l-4.396 4.286a.94.94 0 0 0-.269.831l1.037 6.055a.94.94 0 0 1-1.371.99l-5.416-2.848a.94.94 0 0 0-.874 0l-5.43 2.856a.94.94 0 0 1-1.37-.99L5.8 15.5a.94.94 0 0 0-.27-.832l-4.388-4.289a.939.939 0 0 1 .514-1.603l6.079-.89a.94.94 0 0 0 .708-.514l2.714-5.5a.941.941 0 0 1 1.687 0Z" />
    </svg>
  );
}

function SortIcon() {
  return (
    <svg
      className="h-4 w-4"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      aria-hidden="true"
    >
      <path d="M1 9.304 5.304 5 9.61 9.304M5.26 5v13.998M23 14.696 18.695 19l-4.304-4.304M18.74 19V5.001" />
    </svg>
  );
}

function ShieldIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 12 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9.577 11.57a5.43 5.43 0 0 0 1.645-3.888V.254H0v7.428c0 1.457.584 2.859 1.64 3.888.759.741 1.71 1.25 2.737 1.477-1.194.362-2.132 1.236-2.507 2.34h7.481c-.375-1.1-1.317-1.977-2.511-2.34a5.65 5.65 0 0 0 2.737-1.477"
        fill="#8B9478"
      />
      <path
        d="m5.702 2.723.675 1.772c.02.05.071.075.12.06l1.757-.559c.085-.026.158.068.114.148l-.916 1.652a.105.105 0 0 0 .029.135l1.516 1.076c.074.052.048.17-.04.184L7.14 7.48a.1.1 0 0 0-.083.108l.134 1.9c.007.091-.099.144-.164.081l-1.35-1.293a.095.095 0 0 0-.132 0l-1.35 1.293c-.065.063-.17.01-.164-.081l.134-1.9a.1.1 0 0 0-.083-.108l-1.817-.288c-.087-.014-.113-.132-.04-.184L3.74 5.931a.103.103 0 0 0 .029-.135l-.915-1.652c-.044-.08.029-.174.114-.147l1.757.558a.097.097 0 0 0 .12-.06l.674-1.771a.097.097 0 0 1 .182-.001"
        fill="#fff"
      />
    </svg>
  );
}
